import {useEffect} from 'react'
import PropTypes from 'prop-types'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {deferScriptLoading} from './utils'
import {getSiteEnvVarValue} from './site-config'
import {initBraze, stopBraze} from '../integrations/braze/braze-manager'

// Check Didomi consent status for Braze vendor
const hasConsent = (vendorId) => window.Didomi?.getUserConsentStatusForVendor(vendorId)

export function BrazeScript({brazeUserId}) {
    const {site} = useMultiSite()
    const siteId = site?.id

    useEffect(() => {
        const {app} = getConfig()

        if (getSiteEnvVarValue(app, 'IS_BRAZE_ENABLED', siteId) !== 'true') return undefined

        const vendorId = getSiteEnvVarValue(app, 'BRAZE_DIDOMI_VENDOR_ID', siteId)

        // Idle callbacks outlive the effect, so a schedule made for a previous brazeUserId
        // must not start Braze for a shopper who has since signed in or out.
        let isCurrent = true
        const startBraze = () =>
            deferScriptLoading(() => isCurrent && initBraze(brazeUserId, siteId))

        const onConsent = () => {
            if (hasConsent(vendorId)) {
                // Load Braze after consent is granted
                startBraze()
            } else {
                // Shutdown Braze if consent is revoked
                stopBraze()
            }
        }

        // Initial consent check
        if (hasConsent(vendorId)) {
            startBraze()
        }

        // Listen for consent changes
        if (vendorId && window.Didomi?.on) {
            window.Didomi.on('consent.changed', onConsent)
            return () => {
                isCurrent = false
                window.Didomi?.off?.('consent.changed', onConsent)
            }
        }

        return () => {
            isCurrent = false
        }
    }, [brazeUserId, siteId])

    return null
}

BrazeScript.propTypes = {
    brazeUserId: PropTypes.string
}
